import { useCallback, useEffect, useState } from 'react';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import {
  Folder, FileText, Download, RefreshCw, ChevronRight, Home,
  Loader2, HardDrive, ArrowUp
} from 'lucide-react';
import apiClient from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface BrowseFile {
  key: string;
  name: string;
  size: number;
  sizeFormatted: string;
  lastModified: string;
}

interface BrowseData {
  prefix: string;
  folders: string[];
  files: BrowseFile[];
  nextCursor: string | null;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

/** Nome visuale di una cartella dal suo prefix completo (es. "backups/pg/" → "pg"). */
function folderLabel(fullPrefix: string, currentPrefix: string): string {
  return fullPrefix.slice(currentPrefix.length).replace(/\/$/, '');
}

export default function FileBrowserTab() {
  const { toast } = useToast();
  const [prefix, setPrefix] = useState('');
  const [folders, setFolders] = useState<string[]>([]);
  const [files, setFiles] = useState<BrowseFile[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const load = useCallback(async (targetPrefix: string, cursor?: string) => {
    const isMore = !!cursor;
    if (isMore) setLoadingMore(true); else setLoading(true);
    try {
      const params = new URLSearchParams();
      if (targetPrefix) params.set('prefix', targetPrefix);
      if (cursor) params.set('cursor', cursor);
      const qs = params.toString();
      const res = await apiClient.get<BrowseData>(`/master/storage/browse${qs ? `?${qs}` : ''}`);
      if (res.success && res.data) {
        if (isMore) {
          setFolders((prev) => [...prev, ...res.data!.folders]);
          setFiles((prev) => [...prev, ...res.data!.files]);
        } else {
          setFolders(res.data.folders);
          setFiles(res.data.files);
        }
        setNextCursor(res.data.nextCursor);
      } else {
        toast({ title: 'Errore', description: res.error || 'Caricamento fallito', variant: 'destructive' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore di rete';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      if (isMore) setLoadingMore(false); else setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load(prefix);
  }, [prefix, load]);

  const navigateTo = (newPrefix: string) => {
    setNextCursor(null);
    setPrefix(newPrefix);
  };

  const goUp = () => {
    if (!prefix) return;
    const trimmed = prefix.replace(/\/$/, '');
    const idx = trimmed.lastIndexOf('/');
    navigateTo(idx >= 0 ? trimmed.slice(0, idx + 1) : '');
  };

  const handleDownload = async (key: string) => {
    setDownloadingKey(key);
    try {
      const res = await apiClient.get<{ url: string; expiresInSeconds: number }>(
        `/master/storage/browse/object-url?key=${encodeURIComponent(key)}`
      );
      if (res.success && res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener');
      } else {
        toast({ title: 'Errore', description: res.error || 'URL download non disponibile', variant: 'destructive' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore di rete';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setDownloadingKey(null);
    }
  };

  // Breadcrumb: "" → ["backups/", "backups/pg/"] ecc.
  const crumbs: Array<{ label: string; prefix: string }> = [];
  if (prefix) {
    const parts = prefix.replace(/\/$/, '').split('/');
    let acc = '';
    for (const part of parts) {
      acc += `${part}/`;
      crumbs.push({ label: part, prefix: acc });
    }
  }

  const isEmpty = !loading && folders.length === 0 && files.length === 0;

  return (
    <GlassCard>
      <GlassCardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <GlassCardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Esplora File R2
          </GlassCardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">sola lettura</Badge>
            <Button variant="outline" size="sm" onClick={() => void load(prefix)} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
          </div>
        </div>
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 mt-2 text-sm flex-wrap">
          <button
            type="button"
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => navigateTo('')}
          >
            <Home className="h-3.5 w-3.5" />
            Bucket
          </button>
          {crumbs.map((c) => (
            <span key={c.prefix} className="flex items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              <button
                type="button"
                className={c.prefix === prefix
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground hover:text-foreground transition-colors'}
                onClick={() => navigateTo(c.prefix)}
              >
                {c.label}
              </button>
            </span>
          ))}
        </div>
      </GlassCardHeader>
      <GlassCardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Caricamento...
          </div>
        ) : (
          <div className="space-y-1">
            {prefix && (
              <button
                type="button"
                onClick={goUp}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/50 transition-colors text-left"
              >
                <ArrowUp className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">..</span>
              </button>
            )}

            {folders.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => navigateTo(f)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/50 transition-colors text-left"
              >
                <Folder className="h-4 w-4 text-blue-500 shrink-0" />
                <span className="text-sm font-medium truncate">{folderLabel(f, prefix)}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
              </button>
            ))}

            {files.map((file) => (
              <div
                key={file.key}
                className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/50 transition-colors"
              >
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm truncate flex-1" title={file.key}>{file.name}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap w-20 text-right">{file.sizeFormatted}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap hidden md:inline w-32 text-right">
                  {formatDate(file.lastModified)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={() => void handleDownload(file.key)}
                  disabled={downloadingKey === file.key}
                  title="Scarica"
                >
                  {downloadingKey === file.key
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Download className="h-4 w-4" />}
                </Button>
              </div>
            ))}

            {isEmpty && (
              <div className="text-center py-12 text-muted-foreground text-sm">
                Cartella vuota
              </div>
            )}

            {nextCursor && (
              <div className="pt-3 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void load(prefix, nextCursor)}
                  disabled={loadingMore}
                >
                  {loadingMore && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Carica altri
                </Button>
              </div>
            )}
          </div>
        )}
      </GlassCardContent>
    </GlassCard>
  );
}
